import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AvatarUpload } from '../ui/avatar-upload'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { IbanInput } from '@/components/ui/iban-input'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  createUserByAdmin,
  deleteUserByAdmin,
  getCurrentUserRole,
  getAdminMemberSubscriptionPriceStatuses,
  getCustomerProfiles,
  getManagedUserProfile,
  getStaffProfiles,
  updateProfileByAdmin,
} from '@/lib/api'
import { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '@/lib/constants'
import { isValidIban, normalizeIban } from '@/lib/iban'
import { Loader2, Trash2 } from 'lucide-react'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import type { Profile, UserRole } from '@/types/domain'

const userRoleSchema = z.enum(['member', 'trainer', 'admin'])

const createSchema = z
  .object({
    email: z.string().trim().email('Bitte eine gültige E-Mail-Adresse eingeben.'),
    firstName: z.string().trim().min(1, 'Bitte einen Vornamen eingeben.'),
    lastName: z.string().trim().min(1, 'Bitte einen Nachnamen eingeben.'),
    role: userRoleSchema,
    createMode: z.enum(['invite', 'password']),
    password: z.string(),
    birthDate: z.string(),
    street: z.string(),
    houseNumber: z.string(),
    postalCode: z.string(),
    city: z.string(),
    phoneNumber: z.string(),
    company: z.string(),
    iban: z.string(),
    bankName: z.string(),
    avatarUrl: z.string(),
  })

const editSchema = z.object({
  email: z.string().trim().email('Bitte eine gültige E-Mail-Adresse eingeben.'),
  firstName: z.string().trim().min(1, 'Bitte einen Vornamen eingeben.'),
  lastName: z.string().trim().min(1, 'Bitte einen Nachnamen eingeben.'),
  role: userRoleSchema,
  is_active: z.boolean(),
  birthDate: z.string(),
  street: z.string(),
  houseNumber: z.string(),
  postalCode: z.string(),
  city: z.string(),
  phoneNumber: z.string(),
  company: z.string(),
  iban: z.string(),
  bankName: z.string(),
  avatarUrl: z.string(),
})

const staffCreateRoles: UserRole[] = ['trainer', 'admin']
const allRoles: UserRole[] = ['member', 'trainer', 'admin']
const hiddenContactEmailPlaceholder = 'hidden-contact@example.invalid'

type ManageUsersMode = 'staff' | 'customers'
type SortDirection = 'asc' | 'desc'
type UserSortKey = 'name' | 'email' | 'role' | 'status' | 'created_at'

type UserManagementPageProps = {
  mode: ManageUsersMode
}

type CreateForm = {
  email: string
  firstName: string
  lastName: string
  role: UserRole
  createMode: 'invite' | 'password'
  password: string
  birthDate: string
  street: string
  houseNumber: string
  postalCode: string
  city: string
  phoneNumber: string
  company: string
  iban: string
  bankName: string
  avatarUrl: string
}

type EditForm = {
  email: string
  firstName: string
  lastName: string
  role: UserRole
  is_active: boolean
  birthDate: string
  street: string
  houseNumber: string
  postalCode: string
  city: string
  phoneNumber: string
  company: string
  iban: string
  bankName: string
  avatarUrl: string
}

function toEditFormValues(
  profile: Profile,
  hideContactDataForTrainer: boolean,
): EditForm {
  return {
    email: hideContactDataForTrainer ? hiddenContactEmailPlaceholder : profile.email,
    firstName: profile.first_name,
    lastName: profile.last_name,
    role: profile.role,
    is_active: profile.is_active,
    birthDate: profile.birth_date ?? '',
    street: profile.street ?? '',
    houseNumber: profile.house_number ?? '',
    postalCode: profile.postal_code ?? '',
    city: profile.city ?? '',
    phoneNumber: profile.phone_number ?? '',
    company: profile.company ?? '',
    iban: normalizeIban(profile.iban ?? ''),
    bankName: profile.bank_name ?? '',
    avatarUrl: profile.avatar_url ?? '',
  }
}

function roleLabel(role: UserRole): string {
  switch (role) {
    case 'admin':
      return 'Admin'
    case 'trainer':
      return 'Trainer'
    case 'member':
      return 'Teilnehmer'
    default:
      return role
  }
}

function initialCreateForm(mode: ManageUsersMode): CreateForm {
  return {
    email: '',
    firstName: '',
    lastName: '',
    role: mode === 'staff' ? 'trainer' : 'member',
    createMode: 'invite',
    password: '',
    birthDate: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    phoneNumber: '',
    company: '',
    iban: '',
    bankName: '',
    avatarUrl: '',
  }
}

function createSchemaForMode(mode: ManageUsersMode) {
  return createSchema.superRefine((values, context) => {
    if (mode === 'staff' && !staffCreateRoles.includes(values.role)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['role'],
        message: 'Rolle muss Admin oder Trainer sein.',
      })
    }

    if (mode === 'customers' && values.role !== 'member') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['role'],
        message: 'In der Teilnehmerverwaltung können nur Teilnehmer angelegt werden.',
      })
    }

    if (values.createMode === 'password' && values.password.trim().length < 6) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'Bitte ein Initialpasswort mit mindestens 6 Zeichen eingeben.',
      })
    }

    if (values.role === 'trainer') {
      if (!values.street.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['street'],
          message: 'Straße ist für Trainer erforderlich.',
        })
      }
      if (!values.houseNumber.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['houseNumber'],
          message: 'Hausnummer ist für Trainer erforderlich.',
        })
      }
      if (!values.postalCode.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['postalCode'],
          message: 'PLZ ist für Trainer erforderlich.',
        })
      }
      if (!values.city.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['city'],
          message: 'Stadt ist für Trainer erforderlich.',
        })
      }
      if (!values.phoneNumber.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['phoneNumber'],
          message: 'Telefonnummer ist für Trainer erforderlich.',
        })
      }
    }

    const iban = normalizeIban(values.iban)
    if (iban && !isValidIban(iban)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['iban'],
        message: 'Bitte eine gültige IBAN eingeben.',
      })
    }
  })
}

function editSchemaForMode(mode: ManageUsersMode) {
  return editSchema.superRefine((values, context) => {
    if (mode === 'staff' && !staffCreateRoles.includes(values.role)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['role'],
        message: 'Rolle muss Admin oder Trainer sein.',
      })
    }

    if (mode === 'customers' && values.role !== 'member') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['role'],
        message: 'In der Teilnehmerverwaltung können nur Teilnehmer gepflegt werden.',
      })
    }

    if (values.role === 'trainer') {
      if (!values.street.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['street'],
          message: 'Straße ist für Trainer erforderlich.',
        })
      }
      if (!values.houseNumber.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['houseNumber'],
          message: 'Hausnummer ist für Trainer erforderlich.',
        })
      }
      if (!values.postalCode.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['postalCode'],
          message: 'PLZ ist für Trainer erforderlich.',
        })
      }
      if (!values.city.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['city'],
          message: 'Stadt ist für Trainer erforderlich.',
        })
      }
      if (!values.phoneNumber.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['phoneNumber'],
          message: 'Telefonnummer ist für Trainer erforderlich.',
        })
      }
    }

    const iban = normalizeIban(values.iban)
    if (iban && !isValidIban(iban)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['iban'],
        message: 'Bitte eine gültige IBAN eingeben.',
      })
    }
  })
}

export function UserManagementPage({ mode }: UserManagementPageProps) {
  const queryClient = useQueryClient()

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [loadingEditProfileId, setLoadingEditProfileId] = useState<string | null>(null)
  const createFormMethods = useForm<CreateForm>({
    resolver: zodResolver(createSchemaForMode(mode)),
    defaultValues: initialCreateForm(mode),
  })
  const editFormMethods = useForm<EditForm>({
    resolver: zodResolver(editSchemaForMode(mode)),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: mode === 'staff' ? 'trainer' : 'member',
      is_active: true,
      birthDate: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      phoneNumber: '',
      company: '',
      iban: '',
      bankName: '',
      avatarUrl: '',
    },
  })
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [sortKey, setSortKey] = useState<UserSortKey>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)

  const queryParams = useMemo(
    () => ({
      searchTerm: searchTerm.trim(),
      roleFilter,
      statusFilter,
      sortKey,
      sortDirection,
      page,
      pageSize,
    }),
    [page, pageSize, roleFilter, searchTerm, sortDirection, sortKey, statusFilter],
  )

  const profilesQuery = useQuery({
    queryKey:
      mode === 'staff'
        ? queryKeys.staffProfiles(queryParams)
        : queryKeys.customerProfiles(queryParams),
    queryFn: () =>
      mode === 'staff' ? getStaffProfiles(queryParams) : getCustomerProfiles(queryParams),
    placeholderData: keepPreviousData,
  })

  const currentRoleQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const customerIds = useMemo(
    () => mode === 'customers' ? (profilesQuery.data?.items ?? []).map((profile) => profile.id) : [],
    [mode, profilesQuery.data?.items],
  )

  const subscriptionPriceStatusesQuery = useQuery({
    queryKey: queryKeys.adminMemberSubscriptionPriceStatuses(customerIds),
    queryFn: () => getAdminMemberSubscriptionPriceStatuses(customerIds),
    enabled:
      mode === 'customers' &&
      currentRoleQuery.data?.role === 'admin' &&
      customerIds.length > 0,
  })

  const subscriptionPriceStatusByUserId = useMemo(
    () => new Map(
      (subscriptionPriceStatusesQuery.data ?? []).map((status) => [status.user_id, status]),
    ),
    [subscriptionPriceStatusesQuery.data],
  )

  const createRole = createFormMethods.watch('role')
  const createModeValue = createFormMethods.watch('createMode')
  const createFirstName = createFormMethods.watch('firstName')
  const createLastName = createFormMethods.watch('lastName')
  const createAvatarUrl = createFormMethods.watch('avatarUrl')

  const editRole = editFormMethods.watch('role')
  const editFirstName = editFormMethods.watch('firstName')
  const editLastName = editFormMethods.watch('lastName')
  const editAvatarUrl = editFormMethods.watch('avatarUrl')

  const createMutation = useMutation({
    mutationFn: async (values: CreateForm) => {
      await createUserByAdmin({
        email: values.email.trim().toLowerCase(),
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        role: values.role,
        createMode: values.createMode,
        password: values.createMode === 'password' ? values.password : undefined,
        birth_date: values.birthDate || null,
        street: values.street || null,
        house_number: values.houseNumber || null,
        postal_code: values.postalCode || null,
        city: values.city || null,
        phone_number: values.phoneNumber || null,
        company: values.company || null,
        iban: normalizeIban(values.iban) || null,
        bank_name: values.bankName || null,
        avatar_url: values.avatarUrl || null,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.staffProfilesBase })
      await queryClient.invalidateQueries({ queryKey: queryKeys.customerProfilesBase })
      await queryClient.invalidateQueries({ queryKey: queryKeys.profiles })
      setCreateOpen(false)
      createFormMethods.reset(initialCreateForm(mode))
      toast.success('Nutzer erfolgreich erstellt.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (values: EditForm) => {
      if (!editingProfile) {
        throw new Error('NOT_FOUND')
      }

      await updateProfileByAdmin(editingProfile.id, {
        email: hideContactDataForTrainer ? undefined : values.email.trim().toLowerCase(),
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        role: values.role,
        is_active: values.is_active,
        birth_date: values.birthDate || null,
        street: hideContactDataForTrainer ? undefined : values.street || null,
        house_number: hideContactDataForTrainer ? undefined : values.houseNumber || null,
        postal_code: hideContactDataForTrainer ? undefined : values.postalCode || null,
        city: hideContactDataForTrainer ? undefined : values.city || null,
        phone_number: hideContactDataForTrainer ? undefined : values.phoneNumber || null,
        company: values.company || null,
        iban: hideContactDataForTrainer ? undefined : normalizeIban(values.iban) || null,
        bank_name: hideContactDataForTrainer ? undefined : values.bankName || null,
        avatar_url: values.avatarUrl || null,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.staffProfilesBase })
      await queryClient.invalidateQueries({ queryKey: queryKeys.customerProfilesBase })
      await queryClient.invalidateQueries({ queryKey: queryKeys.profiles })
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
      setEditOpen(false)
      setEditingProfile(null)
      toast.success('Nutzer erfolgreich aktualisiert.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteUserByAdmin(id)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.staffProfilesBase })
      await queryClient.invalidateQueries({ queryKey: queryKeys.customerProfilesBase })
      await queryClient.invalidateQueries({ queryKey: queryKeys.profiles })
      setDeleteConfirmOpen(false)
      setDeleteId(null)
      toast.success('Nutzer erfolgreich gelöscht.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const profiles = useMemo(() => profilesQuery.data?.items ?? [], [profilesQuery.data?.items])
  const totalProfiles = profilesQuery.data?.total ?? 0
  const currentUserId = currentRoleQuery.data?.id ?? null
  const currentUserRole = currentRoleQuery.data?.role ?? null
  const hideContactDataForTrainer = mode === 'customers' && currentUserRole === 'trainer'
  const totalProfilesForDisplay = totalProfiles

  const totalPages = Math.max(1, Math.ceil(totalProfilesForDisplay / pageSize))
  const currentPage = Math.min(page, totalPages)

  const profilesForDisplay = profiles

  const handleEditProfile = async (profile: Profile) => {
    setLoadingEditProfileId(profile.id)
    updateMutation.reset()

    try {
      const freshProfile = await queryClient.fetchQuery({
        queryKey: queryKeys.managedUserProfile(profile.id),
        queryFn: () => getManagedUserProfile(profile.id),
        staleTime: 0,
      })

      setEditingProfile(freshProfile)
      editFormMethods.reset(toEditFormValues(freshProfile, hideContactDataForTrainer))
      setEditOpen(true)
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setLoadingEditProfileId(null)
    }
  }

  const handleSort = (nextKey: UserSortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      setPage(1)
      return
    }

    setSortKey(nextKey)
    setSortDirection('asc')
    setPage(1)
  }

  const sortIndicator = (key: UserSortKey) => {
    if (sortKey !== key) {
      return ''
    }
    return sortDirection === 'asc' ? ' ↑' : ' ↓'
  }

  const title = mode === 'staff' ? 'Mitarbeiterverwaltung' : 'Teilnehmerverwaltung'
  const description =
    mode === 'staff'
      ? 'Hier verwaltest du Trainer und Admins.'
      : 'Hier verwaltest du Teilnehmer. Anlegen erfolgt immer als normaler User.'
  const searchPlaceholder = hideContactDataForTrainer
    ? 'Nach Name, Rolle oder Status filtern ...'
    : 'Nach Name, E-Mail, Rolle oder Status filtern ...'
  const createButtonLabel = mode === 'staff' ? 'Mitarbeiter anlegen' : 'Teilnehmer anlegen'
  const tableContainerClassName = 'max-h-[60dvh] lg:max-h-[calc(100dvh-24rem)]'

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between lg:shrink-0">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <Button
          className="w-full sm:w-auto"
          onClick={() => {
            createMutation.reset()
            createFormMethods.reset(initialCreateForm(mode))
            setCreateOpen(true)
          }}
        >
          {createButtonLabel}
        </Button>
      </div>

      {profilesQuery.isError && (
        <p className="text-sm text-destructive">{getErrorMessage(profilesQuery.error)}</p>
      )}

      <Card className="lg:flex lg:max-h-[calc(100dvh-12.5rem)] lg:min-h-0 lg:flex-col">
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:overflow-hidden">
          <div className="grid gap-3 md:grid-cols-4">
            <Input
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value)
                setPage(1)
              }}
            />
            <Select
              value={roleFilter}
              onValueChange={(value) => {
                setRoleFilter(value as 'all' | UserRole)
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Rollen</SelectItem>
                <SelectItem value="member">Teilnehmer</SelectItem>
                <SelectItem value="trainer">Trainer</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as 'all' | 'active' | 'inactive')
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="inactive">Deaktiviert</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value))
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size} pro Seite
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Table containerClassName={tableContainerClassName}>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button type="button" className="font-medium" onClick={() => handleSort('name')}>
                      Name{sortIndicator('name')}
                    </button>
                  </TableHead>
                  {!hideContactDataForTrainer && (
                    <TableHead>
                      <button type="button" className="font-medium" onClick={() => handleSort('email')}>
                        E-Mail{sortIndicator('email')}
                      </button>
                    </TableHead>
                  )}
                  <TableHead>
                    <button type="button" className="font-medium" onClick={() => handleSort('role')}>
                      Rolle{sortIndicator('role')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="font-medium" onClick={() => handleSort('status')}>
                      Status{sortIndicator('status')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="font-medium" onClick={() => handleSort('created_at')}>
                      Registriert am{sortIndicator('created_at')}
                    </button>
                  </TableHead>
                  {mode === 'customers' && currentUserRole === 'admin' && (
                    <TableHead>Abo-Preis</TableHead>
                  )}
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profilesForDisplay.map((profile) => {
                  const isSelf = profile.id === currentUserId
                  const isEditLoading = loadingEditProfileId === profile.id
                  const isAnyEditLoading = loadingEditProfileId !== null

                  return (
                    <TableRow key={profile.id}>
                      <TableCell>{`${profile.first_name} ${profile.last_name}`}</TableCell>
                      {!hideContactDataForTrainer && <TableCell>{profile.email}</TableCell>}
                      <TableCell>
                        <Badge variant="secondary">{roleLabel(profile.role)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={profile.is_active ? 'default' : 'destructive'}>
                          {profile.is_active ? 'Aktiv' : 'Deaktiviert'}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {new Date(profile.created_at).toLocaleDateString('de-DE')}
                      </TableCell>
                      {mode === 'customers' && currentUserRole === 'admin' && (
                        <TableCell>
                          {(() => {
                            const priceStatus = subscriptionPriceStatusByUserId.get(profile.id)
                            if (!priceStatus) {
                              return <span className="text-muted-foreground">Kein Abo</span>
                            }

                            const label =
                              priceStatus.price_status === 'legacy'
                                ? 'Alter Preis'
                                : priceStatus.price_status === 'pending'
                                  ? 'Ausstehend'
                                  : priceStatus.price_status === 'failed'
                                    ? 'Fehlgeschlagen'
                                    : 'Aktuell'
                            return (
                              <Badge
                                variant={
                                  priceStatus.price_status === 'failed'
                                    ? 'destructive'
                                    : priceStatus.price_status === 'current'
                                      ? 'default'
                                      : 'secondary'
                                }
                              >
                                {label}
                              </Badge>
                            )
                          })()}
                        </TableCell>
                      )}
                      <TableCell className="text-right flex justify-end gap-2">
                        {profile.role === 'member' && (
                          <Button variant="outline" size="sm" asChild>
                            <Link
                              to={
                                currentUserRole === 'trainer'
                                  ? `/trainer/users/${profile.id}`
                                  : `/admin/users/${profile.id}`
                              }
                            >
                              Details
                            </Link>
                          </Button>
                        )}
                        {profile.role === 'trainer' && (
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/admin/trainer/${profile.id}`}>Profil</Link>
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isSelf || isAnyEditLoading}
                          onClick={() => void handleEditProfile(profile)}
                        >
                          {isEditLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Lade ...
                            </>
                          ) : isSelf ? (
                            'Du selbst'
                          ) : (
                            'Bearbeiten'
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isSelf}
                          onClick={() => {
                            setDeleteId(profile.id)
                            setDeleteConfirmOpen(true)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}

                {profilesForDisplay.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={hideContactDataForTrainer ? 5 : 6}
                      className="text-center text-muted-foreground"
                    >
                      Keine Einträge vorhanden.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <p>
              {totalProfilesForDisplay === 0
                ? '0 Ergebnisse'
                : `${(currentPage - 1) * pageSize + 1} - ${Math.min(
                  (currentPage - 1) * pageSize + profilesForDisplay.length,
                  totalProfilesForDisplay,
                )} von ${totalProfilesForDisplay}`}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
              >
                Zurück
              </Button>
              <span>
                Seite {currentPage} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
              >
                Weiter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onOpenChange={(nextOpen) => {
          setCreateOpen(nextOpen)
          if (!nextOpen) {
            createMutation.reset()
            createFormMethods.reset(initialCreateForm(mode))
          }
        }}
      >
        <DialogContent className="flex max-h-[90dvh] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] flex-col overflow-x-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{createButtonLabel}</DialogTitle>
            <DialogDescription>
              Nutzerkonto per Invite-Link oder Initialpasswort erstellen.
            </DialogDescription>
          </DialogHeader>

          <Form {...createFormMethods}>
            <form
              onSubmit={createFormMethods.handleSubmit((values) => {
                createMutation.mutate(values)
              })}
              className="grid min-h-0 flex-1 gap-4 overflow-y-auto py-2 pr-1"
            >
              <FormField
                control={createFormMethods.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="create-email">E-Mail</FormLabel>
                    <FormControl>
                      <Input id="create-email" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={createFormMethods.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="create-first-name">Vorname *</FormLabel>
                      <FormControl>
                        <Input id="create-first-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createFormMethods.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="create-last-name">Nachname *</FormLabel>
                      <FormControl>
                        <Input id="create-last-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={createFormMethods.control}
                name="createMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Erstellmodus</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="invite">Invite-Link senden</SelectItem>
                        <SelectItem value="password">Initialpasswort setzen</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {createModeValue === 'password' && (
                <FormField
                  control={createFormMethods.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="create-password">Initialpasswort</FormLabel>
                      <FormControl>
                        <Input id="create-password" type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={createFormMethods.control}
                name="avatarUrl"
                render={({ field }) => (
                  <FormItem className="space-y-4">
                    <FormLabel>Profilbild</FormLabel>
                    <AvatarUpload
                      currentUrl={createAvatarUrl}
                      fullName={`${createFirstName} ${createLastName}`.trim()}
                      onUploadComplete={(url) => field.onChange(url)}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={createFormMethods.control}
                  name="birthDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="create-birth-date">Geburtsdatum</FormLabel>
                      <FormControl>
                        <DatePickerInput
                          id="create-birth-date"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Geburtsdatum auswählen"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createFormMethods.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="create-company">Anmerkung</FormLabel>
                      <FormControl>
                        <Input id="create-company" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={createFormMethods.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="create-phone">Telefonnummer {createRole === 'trainer' && '*'}</FormLabel>
                      <FormControl>
                        <Input id="create-phone" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {mode === 'staff' && (
                  <FormField
                    control={createFormMethods.control}
                    name="bankName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="create-bank-name">
                          Name der Bank
                        </FormLabel>
                        <FormControl>
                          <Input id="create-bank-name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {mode === 'staff' && (
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={createFormMethods.control}
                    name="iban"
                    render={({ field }) => (
                      <FormItem>
                          <FormLabel htmlFor="create-iban">IBAN</FormLabel>
                          <FormControl>
                          <IbanInput id="create-iban" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                    )}
                  />
                </div>
              )}

              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-3">
                  <FormField
                    control={createFormMethods.control}
                    name="street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="create-street">Straße {createRole === 'trainer' && '*'}</FormLabel>
                        <FormControl>
                          <Input id="create-street" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={createFormMethods.control}
                  name="houseNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="create-house-number">Nr. {createRole === 'trainer' && '*'}</FormLabel>
                      <FormControl>
                        <Input id="create-house-number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={createFormMethods.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor="create-postal-code">PLZ {createRole === 'trainer' && '*'}</FormLabel>
                      <FormControl>
                        <Input id="create-postal-code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="col-span-3">
                  <FormField
                    control={createFormMethods.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="create-city">Stadt {createRole === 'trainer' && '*'}</FormLabel>
                        <FormControl>
                          <Input id="create-city" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={createFormMethods.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rolle</FormLabel>
                    {mode === 'customers' ? (
                      <Input value="Teilnehmer" disabled />
                    ) : (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="trainer">Trainer</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Erstelle ...' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onOpenChange={(nextOpen) => {
          setEditOpen(nextOpen)
          if (!nextOpen) {
            updateMutation.reset()
            setEditingProfile(null)
          }
        }}
      >
        <DialogContent className="flex max-h-[90dvh] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] flex-col overflow-x-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nutzer verwalten</DialogTitle>
            <DialogDescription>
              Name, Rolle und Aktivstatus anpassen.
            </DialogDescription>
          </DialogHeader>

          <Form {...editFormMethods}>
            <form
              onSubmit={editFormMethods.handleSubmit((values) => {
                updateMutation.mutate(values)
              })}
              className="grid min-h-0 flex-1 gap-4 overflow-x-hidden overflow-y-auto py-2 pr-1"
            >
              <div className="min-w-0 space-y-5">
                <section className="min-w-0 space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold">Allgemein</h3>
                    <p className="text-xs text-muted-foreground">
                      Grunddaten, Rolle und Status des Nutzerkontos.
                    </p>
                  </div>

                  <FormField
                    control={editFormMethods.control}
                    name="avatarUrl"
                    render={({ field }) => (
                      <FormItem className="space-y-4">
                        <FormLabel>Profilbild</FormLabel>
                        <AvatarUpload
                          currentUrl={editAvatarUrl}
                          fullName={`${editFirstName} ${editLastName}`.trim()}
                          onUploadComplete={(url) => field.onChange(url)}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!hideContactDataForTrainer && (
                    <FormField
                      control={editFormMethods.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="edit-email">E-Mail</FormLabel>
                          <FormControl>
                            <Input id="edit-email" type="email" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={editFormMethods.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="edit-first-name">Vorname *</FormLabel>
                          <FormControl>
                            <Input id="edit-first-name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editFormMethods.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="edit-last-name">Nachname *</FormLabel>
                          <FormControl>
                            <Input id="edit-last-name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid items-start gap-4 md:grid-cols-2">
                    <FormField
                      control={editFormMethods.control}
                      name="birthDate"
                      render={({ field }) => (
                        <FormItem className="min-w-0 max-w-full">
                          <FormLabel htmlFor="edit-birth-date">Geburtsdatum</FormLabel>
                          <FormControl>
                            <DatePickerInput
                              id="edit-birth-date"
                              value={field.value}
                              onChange={field.onChange}
                              className="min-w-0 max-w-full text-sm"
                              placeholder="Geburtsdatum auswählen"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={editFormMethods.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="edit-company">Anmerkung</FormLabel>
                          <FormControl>
                            <Input id="edit-company" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={editFormMethods.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Rolle</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {allRoles.map((role) => (
                                <SelectItem key={role} value={role}>
                                  {roleLabel(role)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={editFormMethods.control}
                      name="is_active"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            value={field.value ? 'active' : 'inactive'}
                            onValueChange={(value) => field.onChange(value === 'active')}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="active">Aktiv</SelectItem>
                              <SelectItem value="inactive">Deaktiviert</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </section>

                {!hideContactDataForTrainer && (
                  <section className="min-w-0 space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <div className="space-y-1">
                      <h3 className="text-sm font-semibold">Adresse</h3>
                      <p className="text-xs text-muted-foreground">
                        Kontakt- und Adressdaten der Person.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={editFormMethods.control}
                        name="phoneNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="edit-phone">
                              Telefonnummer {editRole === 'trainer' && '*'}
                            </FormLabel>
                            <FormControl>
                              <Input id="edit-phone" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div className="col-span-3">
                        <FormField
                          control={editFormMethods.control}
                          name="street"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel htmlFor="edit-street">
                                Straße {editRole === 'trainer' && '*'}
                              </FormLabel>
                              <FormControl>
                                <Input id="edit-street" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={editFormMethods.control}
                        name="houseNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="edit-house-number">
                              Nr. {editRole === 'trainer' && '*'}
                            </FormLabel>
                            <FormControl>
                              <Input id="edit-house-number" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <FormField
                        control={editFormMethods.control}
                        name="postalCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="edit-postal-code">
                              PLZ {editRole === 'trainer' && '*'}
                            </FormLabel>
                            <FormControl>
                              <Input id="edit-postal-code" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="col-span-3">
                        <FormField
                          control={editFormMethods.control}
                          name="city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel htmlFor="edit-city">
                                Stadt {editRole === 'trainer' && '*'}
                              </FormLabel>
                              <FormControl>
                                <Input id="edit-city" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </section>
                )}

                {!hideContactDataForTrainer && mode === 'staff' && (
                  <section className="min-w-0 space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <div className="space-y-1">
                      <h3 className="text-sm font-semibold">Konto</h3>
                      <p className="text-xs text-muted-foreground">
                        Bankdaten für interne Verwaltung und Abrechnung.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={editFormMethods.control}
                        name="bankName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="edit-bank-name">Name der Bank</FormLabel>
                            <FormControl>
                              <Input id="edit-bank-name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={editFormMethods.control}
                        name="iban"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="edit-iban">IBAN</FormLabel>
                            <FormControl>
                              <IbanInput id="edit-iban" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </section>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Speichere ...' : 'Speichern'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nutzer endgültig löschen?</DialogTitle>
            <DialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Der Nutzer wird vollständig aus dem System entfernt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false)
                setDeleteId(null)
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Löscht ...
                </>
              ) : (
                'Jetzt löschen'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
